import { gql, useMutation, useQuery } from '@apollo/client'

import {
  ArtistRegisterSubscriptionMutation,
  ArtistRegisterSubscriptionMutationVariables,
  ConsoleArtistSubscriptionStartMembershipNumberQuery,
  ConsoleArtistSubscriptionStartMembershipNumberQueryVariables,
  ConsolePlanDetailFragmentDoc,
  ConsoleProductDetailFragmentDoc,
} from '@/clients/utoniq-core/schema'
import { useMessage, useRouter, useTranslation } from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { user } = useCurrentUser()
  const { t } = useTranslation('consoleMembershipCreate')
  const { showMessage } = useMessage()
  const { router, routerPages } = useRouter()

  const { data, loading } = useQuery<
    ConsoleArtistSubscriptionStartMembershipNumberQuery,
    ConsoleArtistSubscriptionStartMembershipNumberQueryVariables
  >(QUERY, {
    variables: { artistId: user?.uid },
    skip: !user?.uid,
  })

  const [artistRegisterSubscription] = useMutation<
    ArtistRegisterSubscriptionMutation,
    ArtistRegisterSubscriptionMutationVariables
  >(ARTIST_REGISTER_SUBSCRIPTION)

  return {
    state: {
      artistId: user?.uid ?? '',
      startMembershipNumber:
        data?.artist?.subscription?.startMembershipNumber ?? null,
    },
    actions: {
      async onSubmit(inputs) {
        const { plans, ...rest } = inputs
        const plansWithMembershipNumberPrefix = plans.map(plan => ({
          ...plan,
          membershipNumberPrefix: plan.membershipNumberPrefix ?? null,
        }))
        await artistRegisterSubscription({
          variables: {
            inputs: {
              ...rest,
              plans: plansWithMembershipNumberPrefix,
              startMembershipNumber:
                rest.startMembershipNumber ===
                data?.artist?.subscription?.startMembershipNumber
                  ? null
                  : rest.startMembershipNumber ?? null,
            },
          },
        })
        showMessage(t('registerSubscriptionSuccess'))
        router.replace(routerPages.ConsoleMembership)
      },
    },
  }
})

const QUERY = gql`
  query ConsoleArtistSubscriptionStartMembershipNumber($artistId: ID) {
    artist(id: $artistId) {
      id
      subscription {
        startMembershipNumber
      }
    }
  }
`

const ARTIST_REGISTER_SUBSCRIPTION = gql`
  mutation ArtistRegisterSubscription($inputs: RegisterSubscriptionsInput!) {
    artistRegisterSubscriptions(inputs: $inputs) {
      id
      subscription {
        plans {
          ...ConsolePlanDetail
        }
        products {
          ...ConsoleProductDetail
        }
      }
    }
  }
  ${ConsolePlanDetailFragmentDoc}
  ${ConsoleProductDetailFragmentDoc}
`
